import React from 'react';

import { isPickable, type ContactPickerState } from '../hooks/useContactPicker';
import type { InvitePerson } from '../services/gmailInviteService';
import { maskEmail, maskName, recordingBlur } from '../utils/recordingBlur';

const SOURCE_LABEL: Record<string, string> = { contact: 'Saved contact', other_contact: 'Emailed before' };
const QUICK_PICKS = [1, 20, 100, 200];

const label = (person: InvitePerson, blur: boolean) => ({
  email: blur ? maskEmail(person.email) : person.email,
  name: person.name && blur ? maskName(person.name) : person.name,
});

const initials = (person: InvitePerson): string => {
  const words = (person.name || person.email).replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);

  return (words[0]?.[0] ?? '?').concat(words[1]?.[0] ?? '').toUpperCase();
};

const note = (person: InvitePerson, general: boolean): string => {
  if (general && person.member) return 'On 8080.AI';
  if (general && person.invited) return 'Invited';

  return SOURCE_LABEL[person.source ?? ''] ?? '';
};

interface RowProps {
  person: InvitePerson;
  general: boolean;
  ticked: boolean;
  full: boolean;
  onToggle: (email: string) => void;
}

const PersonRow: React.FC<RowProps> = ({ person, general, ticked, full, onToggle }) => {
  const shown = label(person, recordingBlur());
  const pickable = isPickable(person, general);

  return (
    <li className={`invite-person${ticked && pickable ? ' is-ticked' : ''}${pickable ? '' : ' is-unavailable'}`}>
      <label>
        <input aria-label={`Invite ${shown.email}`} checked={pickable && ticked} disabled={!pickable || (full && !ticked)}
          onChange={() => onToggle(person.email)} type="checkbox" />
        <span aria-hidden="true" className="invite-person__avatar">{recordingBlur() ? '•' : initials(person)}</span>
        <span className="invite-person__text">
          <span className="invite-person__name">{shown.name || shown.email}</span>
          {shown.name && <span className="invite-person__email">{shown.email}</span>}
        </span>
        <span className="invite-person__note">{note(person, general)}</span>
      </label>
    </li>
  );
};

const QuickPicks: React.FC<{ picker: ContactPickerState; limit: number }> = ({ picker, limit }) => (
  <div aria-label="Quick pick" className="invite-who__quick" role="group">
    <span className="am-note">Quick pick</span>
    {QUICK_PICKS.filter((_, index) => index === 0 || QUICK_PICKS[index - 1] < limit).map((count) => (
      <button aria-pressed={picker.quick === count} className={`am-chip${picker.quick === count ? ' is-active' : ''}`}
        key={count} onClick={() => picker.pick(count)} type="button">Top {Math.min(count, limit)}</button>
    ))}
    <button aria-pressed={picker.quick === 0} className={`am-chip${picker.quick === 0 ? ' is-active' : ''}`}
      onClick={() => picker.pick(0)} type="button">Clear</button>
  </div>
);

interface PeopleListProps {
  picker: ContactPickerState;
  general: boolean;
  limit: number;
}

const PeopleList: React.FC<PeopleListProps> = ({ picker, general, limit }) => {
  if (picker.loading) {
    return <div aria-busy="true" className="invite-skeleton invite-skeleton--rows">Loading your contacts…</div>;
  }
  if (picker.people.length === 0) {
    return <p className="am-note invite-who__empty">No one matches “{picker.query}”.</p>;
  }

  return (
    <ul aria-label="People to invite" className="invite-who__list">
      {picker.people.map((person) => (
        <PersonRow full={picker.selected.size >= limit} general={general} key={person.email}
          onToggle={picker.toggle} person={person} ticked={picker.selected.has(person.email)} />
      ))}
    </ul>
  );
};

const Pager: React.FC<{ picker: ContactPickerState }> = ({ picker }) => (
  <nav aria-label="Pages" className="invite-who__pager">
    <button className="am-secondary" disabled={picker.page <= 1} onClick={() => picker.setPage(picker.page - 1)} type="button">
      Previous
    </button>
    <span>Page {picker.page} of {picker.pages} · {picker.total.toLocaleString()} people</span>
    <button className="am-secondary" disabled={picker.page >= picker.pages} onClick={() => picker.setPage(picker.page + 1)} type="button">
      Next
    </button>
  </nav>
);

interface InviteWhoStepProps {
  picker: ContactPickerState;
  general: boolean;
  limit: number;
  /** The project's name, or 8080.AI. */
  title: string;
  onBack: () => void;
  onDiscard: () => void;
  onReview: () => void;
}

/** Step 2: who to invite — search, quick picks, people page by page, ticks kept across pages. */
export const InviteWhoStep: React.FC<InviteWhoStepProps> = ({
  picker, general, limit, title, onBack, onDiscard, onReview,
}) => (
  <section className="invite-step">
    <div className="am-header">
      <h3 className="am-title">Who should join {title}?</h3>
      <p className="am-lede">Everyone you tick gets their own invitation from your Gmail.</p>
    </div>
    <input aria-label="Search contacts" className="am-input" onChange={(event) => picker.search(event.target.value)}
      placeholder={`Search ${picker.total ? picker.total.toLocaleString() : ''} contacts by name or email`.replace('  ', ' ')}
      type="search" value={picker.query} />
    <QuickPicks limit={limit} picker={picker} />
    <PeopleList general={general} limit={limit} picker={picker} />
    {!picker.loading && picker.pages > 1 && <Pager picker={picker} />}
    <footer className="invite-step__bar">
      <span className="invite-step__count">
        <strong>{picker.selected.size}</strong> selected
        <span className="am-note"> · up to {limit} today</span>
      </span>
      <button className="am-link" onClick={onDiscard} type="button">Discard list</button>
      <button className="am-secondary" onClick={onBack} type="button">Back</button>
      <button className="am-cta" disabled={picker.selected.size === 0} onClick={onReview} type="button">
        Review {picker.selected.size} {picker.selected.size === 1 ? 'invite' : 'invites'}
      </button>
    </footer>
  </section>
);
